import { z } from 'zod';
/**
 * How a listing's Amazon business (B2B) price is derived from the retail
 * price being written. Pure — no Prisma, no fetch — so it is exercised by
 * plain unit tests the same way money.ts and pricing.ts are.
 *
 * There is deliberately no "type it in yourself" mode: the whole point of
 * deriving the business price is that nobody has to keep a second number in
 * sync with the first. A listing that needs its own margin gets a per-listing
 * override of *this same rule shape* (see effectiveRule below), not a
 * standalone value.
 */
export const businessPriceRuleSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('mirror') }),
    /** Percent below the retail price, e.g. 10 for "10% off". */
    z.object({ mode: z.literal('percent_off'), percent: z.coerce.number().min(0).max(90) }),
    /** Minor units below the retail price. */
    z.object({ mode: z.literal('amount_off'), amountMinor: z.coerce.number().int().min(0) }),
]);
export const MIRROR_RETAIL = { mode: 'mirror' };
/**
 * The business price this rule implies for a retail price, in minor units.
 *
 * Clamped to [1, retailMinor]: never above the retail price (a "discount"
 * rule that ends up higher than retail is not a discount) and never below one
 * cent (a steep percent-off on an already-cheap item must not zero out, which
 * a real seller can't submit anyway).
 */
export function deriveBusinessPrice(retailMinor, rule) {
    let derived;
    if (rule.mode === 'mirror') {
        derived = retailMinor;
    }
    else if (rule.mode === 'percent_off') {
        derived = Math.round(retailMinor * (1 - rule.percent / 100));
    }
    else {
        derived = retailMinor - rule.amountMinor;
    }
    return Math.min(retailMinor, Math.max(1, derived));
}
/**
 * The rule actually in force for one listing: its own override if it has one,
 * else the channel-wide rule, else mirror. `listingRule` is whatever is
 * currently stored on ChannelListing.businessPriceRule — untyped Json, so it
 * is parsed rather than trusted; a shape that no longer validates (a rule
 * saved under a schema this code has since changed) falls back the same safe
 * direction getSheetSettings does, rather than throwing mid-write.
 */
export function effectiveRule(listingRule, channelRule) {
    if (listingRule !== null && listingRule !== undefined) {
        const parsed = businessPriceRuleSchema.safeParse(listingRule);
        if (parsed.success)
            return parsed.data;
    }
    return channelRule ?? MIRROR_RETAIL;
}
/** One phrase naming a rule, shared by the admin form, the detail card and the confirm dialog. */
export function businessPriceRuleLabel(rule) {
    if (rule.mode === 'mirror')
        return 'mirrors the retail price';
    if (rule.mode === 'percent_off')
        return `${rule.percent}% below the retail price`;
    return `$${(rule.amountMinor / 100).toFixed(2)} below the retail price`;
}
//# sourceMappingURL=business-price.js.map